/* eslint-disable func-names */
import * as Yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { useDispatch, useSelector } from "react-redux";

import BaseInput from "../../../../../components/elements/BaseInput";
import Button from "../../../../../components/elements/Button";
import FormFieldError from "../../../../../components/elements/FormFieldError";
import classes from "../../PublicationListSelectionSlide.module.scss";
import { BrandgenPublicationList } from "../../../../../types/campaignTypes";
import { DomainRegExp } from "../../../../../constants/regExp";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import { setCreatedCustomDomainList } from "../../../../../redux/reducers/campaignToCreate";
import { successToaster } from "../../../../../helpers/toasters";
import { trimDomains } from "../../helpers";

type CreateCustomDomainListModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  createDomainList: (
    bgAdvertiserId: string,
    adLibAdvertiserId: number,
    name: string,
    domains: string[],
    saveCreatedDomainList: (createdDomainList: BrandgenPublicationList) => void,
  ) => void;
};

type FormInitialValues = {
  name: string;
  domains: string;
};

const CreateCustomDomainListModal: FC<CreateCustomDomainListModalProps> = ({
  isOpen,
  handleClose,
  createDomainList,
}) => {
  const brandgenAdvertiserId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser,
  )?.brandgenAdvertiserId;
  const adLibAdvertiserId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser,
  )?.adLibAdvertiserId;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formInitialValues: FormInitialValues = {
    name: "",
    domains: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Required field."),
    domains: Yup.string()
      .required("Required field.")
      .test("domainsValidation", function (domains) {
        const { path, createError } = this;

        if (domains) {
          const trimmedDomains = trimDomains(domains);
          for (const domain of trimmedDomains) {
            if (!DomainRegExp.test(domain)) {
              return createError({
                path,
                message:
                  "Some of your domains are not valid! Consider that domains should be without HTTP/HTTPS protocol! And have schema like 'example.com'",
              });
            }
          }

          return true;
        }

        return createError({
          path,
          message: "Required field.",
        });
      }),
  });

  const dispatch = useDispatch();

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    const saveCreatedDomainListToStore = (createdDomainList: any) => {
      dispatch(setCreatedCustomDomainList(createdDomainList));
      setIsLoading(false);
      actions.resetForm();
      successToaster(`Domain List "${createdDomainList.name}" is successfully created!`);
      handleClose();
    };

    try {
      setIsLoading(true);
      const { name, domains } = values;
      const trimmedDomains = trimDomains(domains);
      const uniqueDomains = [...new Set(trimmedDomains)];
      await createDomainList(
        brandgenAdvertiserId!,
        adLibAdvertiserId!,
        name,
        uniqueDomains,
        saveCreatedDomainListToStore,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("CreateCustomDomainListModal error: ", error);
      setIsLoading(false);
      actions.resetForm();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Custom Domain List Creation Modal"
      className={classes.dialogContainer}
      maxWidth="sm"
      fullWidth
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ values }) => (
          <>
            <div className={classes.dialogTitle}>
              <Typography variant="h2">Create a New Publication List</Typography>
            </div>

            <Form>
              <DialogContent className={classes.dialogContent}>
                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.name}
                    name="name"
                    variant="outlined"
                    label="Enter name of your list *"
                  />

                  <FormFieldError name="name" />
                </div>

                <div className={classes.textArea}>
                  <Field
                    as={BaseInput}
                    value={values.domains}
                    name="domains"
                    variant="outlined"
                    label="Enter a list of publications (website addresses), separated by space or comma, to add to a list  *"
                    rows={4}
                    multiline
                  />

                  <FormFieldError name="domains" />
                </div>
              </DialogContent>
              <DialogActions>
                <Button className={classes.button} onClick={isLoading ? () => null : handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button className={classes.button} type="submit" isLoading={isLoading} loaderSize={25}>
                  Submit
                </Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateCustomDomainListModal;
